<template>
  <section class="admin-content" id="contact-search">
    <main class="admin-main">
      <section>
        <div class="container">
          <div class="text-center mb-4">
            <h3 class="h3">(Offline) Edit Enrollee</h3>
            <p>Fields marked with <span class="text-danger">*</span> are compulsory</p>
          </div>

          <div class="row">
            <!-- Photo -->
            <div class="col-md-12 mb-3 text-center">
              <img
                :src="getDisplaySrc(editUser.user_image)"
                class="rounded"
                style="width: 120px; height: 120px; object-fit: cover; border: 2px solid #dee2e6;"
                alt="Enrollee Photo"
                onerror="this.onerror=null; this.src='/assets/img/claimify_logo.png'"
              />
              <div class="mt-2">
                <button class="btn btn-outline-secondary btn-sm" type="button" @click="retakePhoto">
                  <i class="fe fe-camera"></i> Retake Photo
                </button>
              </div>
            </div>

            <!-- Sector -->
            <div class="form-group col-md-12">
              <label>Select Sector <span class="text-danger">*</span></label>
              <select class="form-control" v-model="editUser.sectorType">
                <option value="formal">Formal Sector</option>
                <option value="informal">Informal Sector</option>
              </select>
            </div>

            <div class="col-md-12" v-if="editUser.sectorType === 'formal'">
              <div class="form-group">
                <label>Select Formal Sector</label>
                <select class="form-control" v-model="editUser.sector">
                  <option value="State Civil Servant">State Civil Servant</option>
                  <option value="LGA Civil Servant">LGA Civil Servant</option>
                  <option value="Tertiary Student Health Insurance Plan (T-SHIP)">Tertiary Student Health Insurance Plan (T-SHIP)</option>
                </select>
              </div>
            </div>

            <div class="form-group col-md-6" v-if="editUser.sectorType === 'informal'">
              <label>Informal Sector <span class="text-danger">*</span></label>
              <select class="form-control" v-model="editUser.sector">
                <option :value="s.name" v-for="s in sectors" :key="s.agency_id">{{ s.name }}</option>
              </select>
            </div>

            <div class="form-group col-md-6" v-if="editUser.sectorType === 'informal'">
              <label>Special Needs</label>
              <select class="form-control" v-model="editUser.category_of_vulnerable_group">
                <option :value="v.name" v-for="v in vulnerables" :key="v.agency_id">{{ v.name }}</option>
              </select>
            </div>

            <!-- NIN -->
            <div class="form-group col-md-6">
              <label>NIN <span class="text-danger">*</span></label>
              <input
                type="text"
                inputmode="numeric"
                class="form-control"
                maxlength="11"
                v-model="editUser.nimc_number"
                @input="editUser.nimc_number = editUser.nimc_number.replace(/\D/g, '').slice(0, 11)"
                placeholder="NIN (11 digits)"
              />
              <p class="text-danger small" v-if="editUser.nimc_number && editUser.nimc_number.length !== 11">
                NIN must be exactly 11 digits ({{ editUser.nimc_number.length }})
              </p>
            </div>

            <!-- Names -->
            <div class="form-group col-md-6">
              <label>Surname <span class="text-danger">*</span></label>
              <input type="text" class="form-control" v-model="editUser.lastname" placeholder="Last Name" />
            </div>
            <div class="form-group col-md-6">
              <label>First Name <span class="text-danger">*</span></label>
              <input type="text" class="form-control" v-model="editUser.firstname" placeholder="First Name" />
            </div>
            <div class="form-group col-md-6">
              <label>Middle Name</label>
              <input type="text" class="form-control" v-model="editUser.middlename" placeholder="Middle Name" />
            </div>

            <!-- Phone -->
            <div class="form-group col-md-6">
              <label>Phone Number <span class="text-danger">*</span></label>
              <input v-phone
                type="text"
               
                class="form-control"
               
                v-model="editUser.phone_number"
                @input="editUser.phone_number = editUser.phone_number.replace(/\D/g, '').slice(0, 11)"
                placeholder="Phone Number (11 digits)"
              />
              <p class="text-danger small" v-if="editUser.phone_number && editUser.phone_number.length > 0 && editUser.phone_number.length !== 11">
                Phone must be exactly 11 digits ({{ editUser.phone_number.length }})
              </p>
            </div>

            <!-- DOB -->
            <div class="form-group col-md-6">
              <label>Date of Birth <span class="text-danger">*</span></label>
              <input type="date" class="form-control" v-model="editUser.dob" :max="currentDate" />
            </div>

            <!-- Expiry -->
            <div class="form-group col-md-6">
              <label>Expiry Date</label>
              <input type="date" class="form-control" v-model="editUser.expiry_date" />
            </div>

            <!-- MDA (formal only) -->
            <div class="form-group col-md-12" v-if="editUser.sectorType === 'formal'">
              <label>Select MDA</label>
              <v-select
                v-model="editUser.place_of_work"
                :options="mda_offline.data"
                label="name"
                :reduce="item => item.name"
              ></v-select>
            </div>

            <!-- LGA -->
            <div class="form-group col-md-12">
              <label>Select LGA <span class="text-danger">*</span></label>
              <select class="form-control" v-model="editUser.localgovt" @change="onLgaChange">
                <option v-for="loc in osun_lgas.data" :key="loc.id" :value="loc.id">{{ loc.local_name }}</option>
              </select>
            </div>

            <!-- Ward -->
            <div class="form-group col-md-12">
              <label>Select Ward <span class="text-danger">*</span></label>
              <select class="form-control" v-model="editUser.ward">
                <option v-for="w in wards_lga" :key="w.id" :value="w.id">{{ w.ward_name }}</option>
              </select>
            </div>

            <!-- Facility -->
            <div class="form-group col-md-12">
              <label>Principal Facility <span class="text-danger">*</span></label>
              <v-select
                v-model="editUser.provider_id"
                :options="osun_providers.data"
                label="agency_name"
                :reduce="item => item.id"
              ></v-select>
            </div>

            <!-- Gender -->
            <div class="form-group col-md-6">
              <label>Gender <span class="text-danger">*</span></label>
              <select class="form-control" v-model="editUser.gender">
                <option value="Male">Male</option>
                <option value="Female">Female</option>
              </select>
            </div>

            <!-- Blood Group -->
            <div class="form-group col-md-6">
              <label>Blood Group</label>
              <select class="form-control" v-model="editUser.blood">
                <option value="NA">NA</option>
                <option value="A">A</option>
                <option value="B">B</option>
                <option value="AB">AB</option>
                <option value="O">O</option>
              </select>
            </div>

            <!-- Marital Status -->
            <div class="form-group col-md-6">
              <label>Marital Status <span class="text-danger">*</span></label>
              <select class="form-control" v-model="editUser.marital_status">
                <option value="Married">Married</option>
                <option value="Single">Single</option>
                <option value="Widow">Widow</option>
                <option value="Divorced">Divorced</option>
                <option value="Divorced">Separated</option>
              </select>
            </div>

            <!-- Genotype -->
            <div class="form-group col-md-6">
              <label>Genotype</label>
              <select class="form-control" v-model="editUser.genotype">
                <option value="NA">NA</option>
                <option value="AA">AA</option>
                <option value="AS">AS</option>
                <option value="SS">SS</option>
                <option value="AC">AC</option>
              </select>
            </div>

            <!-- Computer Number (formal) -->
            <div class="form-group col-md-6" v-if="editUser.sectorType === 'formal'">
              <label>Staff/Computer Number</label>
              <input type="text" class="form-control" v-model="editUser.salary_number" placeholder="Computer Employment Number" />
            </div>

            <!-- Address -->
            <div class="form-group col-md-12">
              <label>Home Address</label>
              <input type="text" class="form-control" v-model="editUser.address" placeholder="1234 Main St" />
            </div>

            <!-- Actions -->
            <div class="col-md-12 mt-3 mb-5">
              <div class="row">
                <div class="col-md-6">
                  <button class="btn btn-outline-secondary btn-block btn-lg" type="button" @click="goBack">
                    Cancel
                  </button>
                </div>
                <div class="col-md-6">
                  <button class="btn btn-primary btn-block btn-lg" type="button" @click="saveChanges" :disabled="isSaving">
                    {{ isSaving ? 'Saving...' : 'Save Changes' }}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div class="vld-parent">
            <loading :active.sync="isLoading" loader="spinner" :can-cancel="false" :is-full-page="true"></loading>
          </div>
        </div>
      </section>
    </main>
  </section>
</template>

<script>
import { StudentService } from "../../../service/student_service";
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/vue-loading.css";
import { Camera, CameraResultType, CameraSource } from "@capacitor/camera";
import { Capacitor } from "@capacitor/core";
import allfacilitiesJson from "./../../../../public/offline/allfacilities.json";
import lgaswardsJson from "./../../../../public/offline/lgas_wards.json";
import mdaJson from "./../../../../public/offline/mda.json";
import sectorsJson from "@/json/sectors_dynamic.json";
import vulnerablesJson from "@/json/vulnerables.json";

let agency = JSON.parse(localStorage.getItem("agency"));

const EMPTY_USER = {
  id: null,
  firstname: "",
  lastname: "",
  middlename: "",
  nimc_number: "",
  phone_number: "",
  dob: "",
  expiry_date: "",
  gender: "",
  genotype: "NA",
  blood: "NA",
  marital_status: "",
  address: "",
  sector: "",
  sectorType: "informal",
  category_of_vulnerable_group: "",
  provider_id: null,
  localgovt: null,
  ward: null,
  place_of_work: "",
  salary_number: "",
  org_id: null,
  user_image: "",
};

export default {
  components: { Loading },
  name: "OfflineEditForm",

  data() {
    return {
      editUser: { ...EMPTY_USER },
      isSaving: false,
      isLoading: false,
      wards_lga: [],
      osun_lgas: { data: [] },
      osun_providers: { data: [] },
      mda_offline: mdaJson,
      sectors: [],
      vulnerables: [],
      service: new StudentService(),
    };
  },

  computed: {
    currentDate() {
      const today = new Date();
      const dd = String(today.getDate()).padStart(2, "0");
      const mm = String(today.getMonth() + 1).padStart(2, "0");
      return `${today.getFullYear()}-${mm}-${dd}`;
    },
  },

  mounted() {
    const saved = localStorage.getItem("editUser");
    if (saved) {
      this.editUser = { ...EMPTY_USER, ...JSON.parse(saved) };
    }

    // filter LGAs by agency state
    const allLgas = lgaswardsJson.data;
    this.osun_lgas = { data: allLgas.filter(x => x.state_id == (agency && agency.state)) };

    // filter facilities by agency state
    const allFacilities = allfacilitiesJson.data;
    this.osun_providers = { data: allFacilities.filter(x => x.state == (agency && agency.state)) };

    // filter sectors & vulnerables by agency
    this.sectors = sectorsJson.filter(x => x.agency_id == (agency && agency.id));
    this.vulnerables = vulnerablesJson.filter(x => x.agency_id == (agency && agency.id));

    // pre-load wards for the saved LGA
    if (this.editUser.localgovt) {
      this.loadWards(this.editUser.localgovt);
    }
  },

  methods: {
    loadWards(lgaId) {
      const lga = this.osun_lgas.data.find(x => x.id == lgaId);
      this.wards_lga = lga ? (lga.wards || []) : [];
    },

    onLgaChange() {
      this.loadWards(this.editUser.localgovt);
      this.editUser.ward = null;

      // narrow facilities to selected LGA
      const all = allfacilitiesJson.data;
      const filtered = all.filter(x => x.localgovt == this.editUser.localgovt);
      this.osun_providers = { data: filtered.length ? filtered : all.filter(x => x.state == (agency && agency.state)) };
    },

    getDisplaySrc(path) {
      if (!path) return "/assets/img/claimify_logo.png";
      if (path.startsWith("data:") || path.startsWith("blob:") || path.startsWith("http")) return path;
      try {
        return Capacitor.convertFileSrc(path);
      } catch {
        return path;
      }
    },

    async retakePhoto() {
      try {
        const image = await Camera.getPhoto({
          quality: 50,
          allowEditing: false,
          resultType: CameraResultType.Uri,
          source: CameraSource.Camera,
        });
        this.editUser.user_image = image.path || image.webPath;
        this.$toasted.info("Photo updated!", { position: "top-center", duration: 3000 });
      } catch (err) {
        if (err && err.message !== "User cancelled photos app") {
          this.$toasted.error("Could not access camera.", { position: "top-center", duration: 3000 });
        }
      }
    },

    validate() {
      if (!this.editUser.nimc_number || this.editUser.nimc_number.length !== 11) {
        this.$toasted.error("NIN must be exactly 11 digits", { position: "top-center", duration: 4000 });
        return false;
      }
      if (!this.editUser.phone_number || this.editUser.phone_number.length !== 11) {
        this.$toasted.error("Phone number must be exactly 11 digits", { position: "top-center", duration: 4000 });
        return false;
      }
      if (!this.editUser.firstname || !this.editUser.lastname) {
        this.$toasted.error("First name and surname are required", { position: "top-center", duration: 4000 });
        return false;
      }
      return true;
    },

    async saveChanges() {
      if (!this.validate()) return;
      this.isSaving = true;
      try {
        const updated = {
          ...this.editUser,
          firstname: this.editUser.firstname.toUpperCase(),
          lastname: this.editUser.lastname.toUpperCase(),
        };
        await this.service.updateStudentById(updated);
        localStorage.removeItem("editUser");
        this.$emit("update-item");
        this.$toasted.success("Enrollee updated successfully!", { position: "top-center", duration: 3000 });
        this.$router.push("/add-client/employee-offline");
      } catch (ex) {
        this.$toasted.error(ex.message || "Update failed", { position: "top-center", duration: 4000 });
      } finally {
        this.isSaving = false;
      }
    },

    goBack() {
      localStorage.removeItem("editUser");
      this.$router.push("/add-client/employee-offline");
    },
  },
};
</script>

<style scoped>
.spacer {
  margin-left: 5px;
  margin-top: 5px;
}
</style>
